// Work Orders › Shrinkwrap: every shrinkwrap install this season, grouped by how far along it is
// (shrinkwrapCore.js has the stage rules), plus the season's "Total shrinkwrap needed" by width.
// One row per boat; click a row to open the op (its stage track and wrap size live in the op panel).
// Live: redraws in place when jobs or boats change (docs/live-updates-plan.md).

import { idbCursorAll, escapeHTML, woLabel } from './utilities';
import { patchHTML, liveRefresher, applyLive, onLocalDataChanged } from './liveView';
import { formatDateToYYYYMMDD } from './date-format';
import { STAGES, buildShrinkwrapBoard, wrapTotals, wrapTotalsHTML, stageCounts, fmtWrap } from './shrinkwrapCore';

let deps = { opCheck: () => {} };
/** app.js hands in opCheck (circular import otherwise). */
export function setShrinkwrapViewDeps(d) { deps = { ...deps, ...d }; }

let stageFilter = 'all';
let showDone = false;
let jobsById = new Map();

const STAGE_HINT = {
  'not-hauled': 'Still in the water.',
  waiting: 'Hauled, but other work on the winterizing work order is still open.',
  ready: 'Hauled and every other op on the work order is done.',
  framed: 'Frame built.',
  vacuumed: 'Bilge and compartments vacuumed.',
  covered: 'Film on and taped.',
  tightened: 'Shrunk tight and inspected.',
  review: 'Vents in. Office to review and bill.',
  done: 'Ready to bill, billed or closed.',
};

const viewShown = () => {
  const v = document.getElementById('shrinkwrap-view');
  return !!v && v.style.display !== 'none';
};

async function loadRows() {
  const [jobs, customers] = await Promise.all([idbCursorAll('jobData'), idbCursorAll('customerData')]);
  jobsById = new Map(jobs.filter(j => j && j.id).map(j => [j.id, j]));
  const customersById = new Map(customers.map(c => [c.Customer_ID, c]));
  return buildShrinkwrapBoard(jobs, customersById, formatDateToYYYYMMDD(new Date()));
}

function matchesSearch(row, q) {
  if (!q) return true;
  return `${row.boatName} ${row.customer} ${row.workOrder} ${woLabel(row.workOrder)} ${row.techs}`.toLowerCase().includes(q);
}

function fmtDay(ymd) {
  if (!ymd) return '';
  const [, m, d] = ymd.split('-');
  return `${Number(m)}/${Number(d)}`;
}

function cutHTML(row) {
  if (row.recorded) {
    return `<span class="sw-row-cut sw-row-cut--recorded" title="Size used last time">
      <span class="sw-row-cut-label">Used${row.recorded.date ? ` ${escapeHTML(row.recorded.date.slice(0, 4))}` : ' before'}</span>
      <span class="sw-row-cut-size">${escapeHTML(fmtWrap(row.recorded))}</span>
    </span>`;
  }
  if (row.estimate) {
    return `<span class="sw-row-cut" title="Estimate from length and beam">
      <span class="sw-row-cut-label">Est.</span>
      <span class="sw-row-cut-size">${escapeHTML(fmtWrap(row.estimate))}</span>
    </span>`;
  }
  return '<span class="sw-row-cut sw-row-cut--none"><span class="sw-row-cut-label">No length or beam on file</span></span>';
}

function rowHTML(row) {
  const dims = row.loa ? `${row.loa}'${row.beam ? ` × ${row.beam}'` : ''}` : '';
  const chips = [];
  if (row.estimate?.over && !row.recorded) chips.push('<span class="sw-chip sw-chip--wait" title="Wider than 40 ft of film">Seam two pieces</span>');
  if (row.hold) chips.push('<span class="sw-chip sw-chip--hold">On hold</span>');
  if (row.stage === 'not-hauled' && row.haulDate) chips.push(`<span class="sw-chip">Haul ${escapeHTML(fmtDay(row.haulDate))}</span>`);
  if (row.stage === 'waiting') {
    const names = row.blockers.slice(0, 3).map(b => escapeHTML(b.title.toLowerCase())).join(', ');
    chips.push(`<span class="sw-chip sw-chip--wait" title="${escapeHTML(row.blockers.map(b => `${b.title} (${b.status})`).join('\n'))}">${row.blockers.length} open: ${names}${row.blockers.length > 3 ? '…' : ''}</span>`);
  }
  if (row.techs) chips.push(`<span class="sw-chip sw-chip--tech">${escapeHTML(row.techs)}</span>`);
  return `
    <div class="sw-row" data-key="${escapeHTML(row.id)}" data-op-id="${escapeHTML(row.id)}" tabindex="0" role="button">
      <span class="sw-row-boat">${escapeHTML(row.boatName || 'Boat')}</span>
      <span class="sw-row-owner">${escapeHTML(row.customer)}</span>
      <span class="sw-row-dims">${escapeHTML(dims)}</span>
      ${cutHTML(row)}
      <span class="sw-row-chips">${chips.join('')}</span>
      <span class="sw-row-wo">${escapeHTML(woLabel(row.workOrder))}</span>
    </div>`;
}

function stageBarHTML(counts, total) {
  const chip = (key, label, n) => `<button type="button" class="sw-stage-chip${stageFilter === key ? ' active' : ''}${n ? '' : ' sw-stage-chip--empty'}" data-key="chip-${key}" data-stage="${key}">${escapeHTML(label)} <span class="sw-stage-count">${n}</span></button>`;
  return chip('all', 'All', total) + STAGES.map(s => chip(s.key, s.label, counts[s.key] || 0)).join('<span class="sw-stage-arrow">›</span>');
}

function groupsHTML(rows) {
  const groups = STAGES.filter(s => stageFilter === 'all' || stageFilter === s.key).map(s => {
    const list = rows.filter(r => r.stage === s.key)
      .sort((a, b) => (a.haulDate || '9').localeCompare(b.haulDate || '9') || a.boatName.localeCompare(b.boatName));
    if (!list.length) return '';
    const collapsed = s.key === 'done' && stageFilter !== 'done' && !showDone;
    return `
      <section class="sw-group sw-group--${s.key}" data-key="group-${s.key}">
        <div class="sw-group-head">
          <span class="sw-group-dot"></span>
          <span class="sw-group-title">${escapeHTML(s.label)}</span>
          <span class="sw-group-count">${list.length}</span>
          <span class="sw-group-hint">${escapeHTML(STAGE_HINT[s.key])}</span>
          ${s.key === 'done' && stageFilter !== 'done' ? `<button type="button" class="sw-group-toggle" data-key="done-toggle" data-done-toggle>${collapsed ? 'Show' : 'Hide'}</button>` : ''}
        </div>
        ${collapsed ? '' : `<div class="sw-rows">${list.map(rowHTML).join('')}</div>`}
      </section>`;
  }).join('');
  return groups || `<div class="sw-empty">${rows.length ? 'Nothing at this stage.' : 'No shrinkwrap installs this season yet.'}</div>`;
}

function draw(rows) {
  const q = (document.getElementById('shrinkwrap-search')?.value || '').trim().toLowerCase();
  const shown = rows.filter(r => matchesSearch(r, q));
  patchHTML(document.getElementById('sw-stage-bar'), stageBarHTML(stageCounts(shown), shown.length));
  patchHTML(document.getElementById('sw-groups'), groupsHTML(shown));
  patchHTML(document.getElementById('sw-totals-body'), wrapTotalsHTML(wrapTotals(rows)));
  wire();
}

function wire() {
  document.querySelectorAll('#sw-stage-bar [data-stage]').forEach(btn => {
    btn.onclick = () => { stageFilter = btn.dataset.stage; renderShrinkwrapView(); };
  });
  document.querySelectorAll('#sw-groups [data-done-toggle]').forEach(btn => {
    btn.onclick = () => { showDone = !showDone; renderShrinkwrapView(); };
  });
  document.querySelectorAll('#sw-groups .sw-row').forEach(card => {
    const open = () => {
      const job = jobsById.get(card.dataset.opId);
      if (job) deps.opCheck(null, job);
    };
    card.onclick = open;
    card.onkeydown = (e) => { if (e.key === 'Enter') open(); };
  });
}

let lastRows = [];
export async function renderShrinkwrapView() {
  if (!document.getElementById('sw-groups')) return;
  lastRows = await loadRows();
  draw(lastRows);
}

/** Dashboard › Shrinkwrap card or a link: open on one stage. */
export function setShrinkwrapStage(stage) {
  stageFilter = STAGES.some(s => s.key === stage) ? stage : 'all';
}

document.getElementById('shrinkwrap-search')?.addEventListener('input', () => draw(lastRows));

const live = liveRefresher({
  refresh: async (isBusy) => {
    if (!viewShown()) return;
    const rows = await loadRows();
    if (isBusy()) { setTimeout(live, 0); return; }
    lastRows = rows;
    applyLive(() => draw(rows));
  },
});
onLocalDataChanged(live);
